import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { client } from '../axiosClient';
import { Button } from './ui/button';
import { Card } from './ui/card.jsx';
import onboarding1 from '../assets/onboarding1.png';
import onboarding2 from '../assets/onboarding2.png';
import onboarding3 from '../assets/onboarding3.png';
import { toast } from './ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import girlyCloset from '../assets/girlyCloset.png';
import vintageCloset from '../assets/vintageCloset.png';
import chicCloset from '../assets/chicCloset.png';
import bohemianCloset from '../assets/bohemianCloset.jpg';

/**
 * PostHog Events Tracked in WelcomeOnboarding:
 * 
 * 1. onboarding_started - When user enters onboarding
 * 2. onboarding_intro_step_viewed - Each intro step viewed
 * 3. onboarding_intro_completed - All intro steps completed
 * 4. onboarding_gender_selected - Gender selection confirmed
 * 5. onboarding_step_transition - Step transitions
 * 6. onboarding_male_selected_demo_path / onboarding_female_selected_demo_path - Demo path selection
 * 7. onboarding_male_selected_empty_path / onboarding_female_selected_empty_path - Empty path selection
 * 8. onboarding_demo_closets_fetched - Demo closets loaded
 * 9. onboarding_demo_closets_error - Demo closets fetch error
 * 10. onboarding_confirmation_cancelled - Dialog cancelled
 * 11. onboarding_confirmation_confirmed - Dialog confirmed
 * 12. onboarding_male_started_with_demo_collection / onboarding_female_started_with_demo_collection - Demo collection applied
 * 13. onboarding_male_started_with_empty_closet / onboarding_female_started_with_empty_closet - Empty closet applied
 * 14. onboarding_male_started_with_demo_collection_error / onboarding_female_started_with_demo_collection_error - Demo application errors
 * 15. onboarding_male_started_with_empty_closet_error / onboarding_female_started_with_empty_closet_error - Empty closet errors
 * 16. onboarding_back_navigation - Back button usage
 * 17. onboarding_validation_error - Form validation errors
 * 18. onboarding_intro_skipped - Intro steps skipped (user still needs to complete gender/path)
 * 19. onboarding_error - General onboarding errors
 */

const ONBOARDING_DATA = [
  {
    title: 'AI-Powered Style Analysis',
    description: 'Upload your clothes and get instant AI categorization with background removal and smart tagging',
    image: onboarding1,
    gradient: 'from-blue-500 to-purple-600'
  },
  {
    title: 'Smart Outfit Suggestions',
    description: 'Get outfit recommendations based on your prompt',
    image: onboarding2,
    gradient: 'from-purple-500 to-pink-600'
  },
  {
    title: 'Virtual Closet Management',
    description: 'Organize your closet digitally, track your outfits, and discover new styling possibilities',
    image: onboarding3,
    gradient: 'from-green-500 to-blue-600'
  }
];

const WelcomeOnboarding = ({ onComplete }) => {
  const [step, setStep] = useState('intro'); // 'intro', 'gender_selection', 'choose_path'
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedGender, setSelectedGender] = useState(null);
  const [closetType, setClosetType] = useState(null); // 'demo' or 'empty'
  const [demoClosets, setDemoClosets] = useState([]);
  const [selectedDemoCloset, setSelectedDemoCloset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null); // 'demo' or 'empty'
  const navigate = useNavigate();
  const posthog = usePostHog();

  // Add style mapping constant
  const STYLE_IMAGES = {
    'Girly Collection': girlyCloset,
    'Vintage Collection': vintageCloset,
    'Chic Collection': chicCloset,
    'Bohemian Collection': bohemianCloset,
  };

  // Track onboarding start (user is already identified in App.js)
  useEffect(() => {
    posthog.capture('onboarding_started', {
      step: 'intro',
      current_step: 0
    });
  }, [posthog]);

  const handleNext = () => {
    if (currentStep < ONBOARDING_DATA.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Track intro step progression
      posthog.capture('onboarding_intro_step_viewed', {
        current_step: currentStep,
        next_step: nextStep,
        step_title: ONBOARDING_DATA[currentStep].title
      });
    } else {
      setStep('gender_selection');
      
      // Track transition to gender selection
      posthog.capture('onboarding_intro_completed', {
        step: 'intro',
        total_intro_steps: ONBOARDING_DATA.length,
        next_step: 'gender_selection'
      });
    }
  };

  const handleSkip = () => {
    // Track onboarding intro skip
    posthog.capture('onboarding_intro_skipped', {
      current_step: currentStep,
    });
    
    // Skip only intro steps, go to gender selection
    setStep('gender_selection');
  };

  const handleCompleteOnboarding = async () => {
    try {
      setIsLoading(true);
      await client.patch('/api/profile/update/', { has_completed_onboarding: true });
      if (onComplete) {
        onComplete();
      }
      navigate('/pricing');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast({
        title: "Error",
        description: "Failed to complete onboarding. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add this function to fetch demo closets based on gender
  const fetchDemoClosets = async (gender) => {
    try {
      const response = await client.get(`/api/demo-closets/?gender=${gender}`);
      setDemoClosets(response.data);
      
      // Track demo closets fetch
      posthog.capture('onboarding_demo_closets_fetched', {
        gender: gender
      });
    } catch (error) {
      console.error('Error fetching demo closets:', error);
      
      // Track demo closets fetch error
      posthog.capture('onboarding_demo_closets_error', {
        gender: gender,
        error_message: error.message,
        step: 'choose_path'
      });
      
      toast({
        title: "Error",
        description: "Failed to fetch demo closets. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Add this function to handle demo closet application
  const applyDemoCloset = async (closetId) => {
    try {
      setIsLoading(true);
      
      // Find the selected demo closet for tracking
      const selectedCloset = demoClosets.find(closet => closet.id === closetId);
      
      // First apply the demo closet
      await client.post(`/api/demo-closets/${closetId}/apply-to-user/`);
      
      // Then update user profile and complete onboarding
      await client.patch('/api/profile/update/', { 
        has_completed_onboarding: true,
        gender: selectedGender 
      });

      // Track demo closet application and onboarding completion with gender-specific event name
      if (selectedGender) {
        const eventName = `onboarding_${selectedGender}_started_with_demo_collection`;
        posthog.capture(eventName, {
          gender: selectedGender,
          demo_closet_id: closetId,
          demo_closet_name: selectedCloset?.name,
          demo_closet_items_count: selectedCloset?.outfits_count
        });
      }

      // Call onComplete callback if it exists
      if (onComplete) {
        onComplete();
      }

      // Finally navigate to pricing page
      navigate('/pricing');
    } catch (error) {
      console.error('Error applying demo closet:', error);
      
      // Track demo closet application error with gender-specific event name
      if (selectedGender) {
        const eventName = `onboarding_${selectedGender}_started_with_demo_collection_error`;
        posthog.capture(eventName, {
          demo_closet_id: closetId,
          gender: selectedGender,
          error_message: error.message
        });
      }
      
      toast({
        title: "Error",
        description: "Failed to apply demo closet. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Modify handleConfirm function
  const handleConfirm = async () => {
    if (confirmationType === 'empty') {
      try {
        await client.patch('/api/profile/update/', { has_completed_onboarding: true, gender: selectedGender });
        
        // Track empty closet selection and onboarding completion with gender-specific event name
        if (selectedGender) {
          const eventName = `onboarding_${selectedGender}_started_with_empty_closet`;
          posthog.capture(eventName, {
            gender: selectedGender,
          });
        }
        
        if (onComplete) {
          onComplete();
        }
        navigate('/pricing');
      } catch (error) {
        console.error('Error completing onboarding:', error);
        
        // Track empty closet completion error with gender-specific event name
        if (selectedGender) {
          const eventName = `onboarding_${selectedGender}_started_with_empty_closet_error`;
          posthog.capture(eventName, {
            gender: selectedGender,
            error_message: error.message
          });
        }
        
        toast({
          title: "Error",
          description: "Failed to complete onboarding. Please try again.",
          variant: "destructive"
        });
      }
    } else if (confirmationType === 'demo') {
      try {
        setIsLoading(true);
        await applyDemoCloset(selectedDemoCloset);
      } catch (error) {
        console.error('Error applying demo closet:', error);
        toast({
          title: "Error",
          description: "Failed to apply demo closet. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    }
    setShowConfirmDialog(false);
  };

  // Modify the demo closet apply handler
  const handleDemoClosetSelection = (closetId) => {
    const selectedCloset = demoClosets.find(closet => closet.id === closetId);
    
    // Track demo closet selection with gender-specific event name
    if (selectedGender) {
      const eventName = `onboarding_${selectedGender}_selected_demo_path`;
      posthog.capture(eventName, {
        demo_collection_id: closetId,
        demo_collection_name: selectedCloset?.name,
        demo_collection_items_count: selectedCloset?.outfits_count,
        gender: selectedGender
      });
    }
    
    setSelectedDemoCloset(closetId);
    setConfirmationType('demo');
    setShowConfirmDialog(true);
  };

  // Add this function definition
  const handleEmptyClosetSelect = () => {
    // Track empty closet selection with gender-specific event name
    if (selectedGender) {
      const eventName = `onboarding_${selectedGender}_selected_empty_path`;
      posthog.capture(eventName, {
        gender: selectedGender,
      });
    }
    
    setConfirmationType('empty');
    setShowConfirmDialog(true);
  };

  // Handle gender selection (only set state, don't track yet)
  const handleGenderSelect = (gender) => {
    setSelectedGender(gender);
  };

  // Handle step transitions
  const handleStepTransition = (fromStep, toStep) => {
    // Track step transitions
    posthog.capture('onboarding_step_transition', {
      from_step: fromStep,
      to_step: toStep,
      gender: selectedGender
    });
    
    setStep(toStep);
  };

  // Handle closet type selection
  const handleClosetTypeSelect = (type) => {
    setClosetType(type);
    
    // Track closet type selection with gender-specific event names
    if (selectedGender && type) {
      const eventName = `onboarding_${selectedGender}_selected_${type}_path`;
      posthog.capture(eventName, {
        closet_type: type,
        gender: selectedGender,
      });
    }
    
    if (type === 'demo' && selectedGender) {
      fetchDemoClosets(selectedGender);
    }
  };

  // Back button component
  const BackButton = ({ onClick, fromStep, toStep }) => (
    <button
      onClick={() => {
        // Track back navigation
        posthog.capture('onboarding_back_navigation', {
          from_step: fromStep,
          to_step: toStep,
          gender: selectedGender
        });
        onClick();
      }}
      className="absolute top-6 left-6 text-gray-600 hover:text-gray-900 flex items-center gap-2 transition-colors"
    >
      <svg 
        className="w-5 h-5" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
      <span>Back</span>
    </button>
  );

  if (step === 'intro') {
    const currentData = ONBOARDING_DATA[currentStep];

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-8 space-y-8 relative">
          {/* Skip Button */}
          <button
            onClick={handleSkip}
            className="absolute top-6 right-6 text-gray-300 hover:text-gray-700 transition-colors text-xs"
          >
            Skip
          </button>

          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Header */}
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Welcome to Stylegen AI
              </h1>
            </div>

            {/* Main Content */}
            <div className="text-center space-y-6">
              <div className="relative mx-auto w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-lg">
                <div className={`absolute inset-0 bg-gradient-to-br ${currentData.gradient} opacity-10`} />
                <img
                  src={currentData.image}
                  alt={currentData.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {currentData.title}
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed px-2">
                  {currentData.description}
                </p>
              </div>
            </div>

            {/* Progress Indicators */}
            <div className="flex justify-center items-center space-x-3">
              {ONBOARDING_DATA.map((_, index) => (
                <div
                  key={index}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    currentStep === index 
                      ? 'w-8 bg-blue-500' 
                      : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Action Button */}
            <div className="flex justify-center">
              <Button
                onClick={handleNext}
                disabled={isLoading}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-full hover:from-purple-500 hover:to-blue-600 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Setting up...
                  </div>
                ) : currentStep === ONBOARDING_DATA.length - 1 ? (
                  'Get Started'
                ) : (
                  'Next'
                )}
              </Button>
            </div>
          </motion.div>
        </Card>
      </div>
    );
  }

  if (step === 'gender_selection') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4 relative">
        <BackButton 
          onClick={() => setStep('intro')} 
          fromStep="gender_selection"
          toStep="intro"
        />
        <Card className="max-w-md w-full p-8 space-y-6 bg-white shadow-xl rounded-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Select Your Gender
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-2">This helps us tailor your experience.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => handleGenderSelect('male')}
                className={`relative overflow-hidden rounded-2xl border-2 hover:border-blue-300 transition-all duration-300 p-6 bg-white group ${
                  selectedGender === 'male' ? 'border-blue-500 shadow-lg' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`transition-transform ${
                    selectedGender === 'male' ? 'text-blue-600 scale-110' : 'text-blue-500 group-hover:scale-110'
                  }`}>
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-gray-900">Male</span>
                </div>
              </button>

              <button
                onClick={() => handleGenderSelect('female')}
                className={`relative overflow-hidden rounded-2xl border-2 hover:border-purple-300 transition-all duration-300 p-6 bg-white group ${
                  selectedGender === 'female' ? 'border-purple-500 shadow-lg' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`transition-transform ${
                    selectedGender === 'female' ? 'text-purple-600 scale-110' : 'text-purple-500 group-hover:scale-110'
                  }`}>
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-gray-900">Female</span>
                </div>
              </button>
            </div>

            <Button
              onClick={() => {
                if (selectedGender) {
                  // Track gender selection only when user confirms by clicking Continue
                  posthog.capture(`onboarding_${selectedGender}_selected`);
                  
                  handleStepTransition('gender_selection', 'choose_path');
                } else {
                  // Track validation error
                  posthog.capture('onboarding_validation_error', {
                    error_type: 'gender_not_selected',
                    step: 'gender_selection'
                  });
                  toast({ title: "Please select a gender", variant: "destructive" });
                }
              }}
              disabled={!selectedGender}
              className="w-full mt-6 bg-blue-600 text-white font-semibold py-3 rounded-full shadow-md hover:shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
            >
              Continue
            </Button>
          </motion.div>
        </Card>
      </div>
    );
  }

  if (step === 'choose_path') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative max-w-4xl mx-auto">
        <BackButton 
          onClick={() => {
            setStep('gender_selection');
            // Reset closet-related state when going back to gender selection
            setClosetType(null);
            setDemoClosets([]);
          }} 
          fromStep="choose_path"
          toStep="gender_selection"
        />
        <Card className="max-w-2xl w-full p-8">
          <div className="space-y-6">
            {!closetType ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h2 className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                    Choose Your Path
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Demo Closet Option */}
                  <button
                    onClick={() => {
                      if (selectedGender) {
                        handleClosetTypeSelect('demo');
                      } else {
                        // Track validation error
                        posthog.capture('onboarding_validation_error', {
                          error_type: 'gender_not_selected_for_demo',
                          step: 'choose_path'
                        });
                        toast({ title: "Gender Not Selected", description: "Please go back and select a gender first.", variant: "destructive" });
                      }
                    }}
                    className="group relative overflow-hidden rounded-2xl border-2 border-transparent hover:border-purple-300 transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-600"
                  >
                    <div className="relative p-8 text-white">
                      <div className="flex items-center gap-4 mb-4">
                        <svg className="w-8 h-8 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className="text-xl sm:text-2xl font-bold">Demo Closet</span>
                      </div>
                      <p className="text-base sm:text-lg opacity-90 mb-4">
                        Don't have photos of your outfits yet?
                      </p>
                      <div className="inline-block rounded-full bg-white/20 px-4 py-2 text-sm font-medium">
                        Start with pre-curated outfits
                      </div>
                    </div>
                  </button>

                  {/* Empty Closet Option */}
                  <button
                    onClick={handleEmptyClosetSelect}
                    className="group relative overflow-hidden rounded-2xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-300 bg-white"
                  >
                    <div className="relative p-8 text-gray-800">
                      <div className="flex items-center gap-4 mb-4">
                        <svg className="w-8 h-8 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span className="text-xl sm:text-2xl font-bold">Empty Closet</span>
                      </div>
                      <p className="text-base sm:text-lg opacity-80 mb-4">
                        Have photos of your outfits ready?
                      </p>
                      <div className="inline-block rounded-full bg-gray-100 px-4 py-2 text-sm font-medium">
                        Upload your collection from scratch
                      </div>
                    </div>
                  </button>
                </div>
              </motion.div>
            ) : closetType === 'demo' && selectedGender && demoClosets.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Back Button for Demo Closet Selection */}
                <button
                  onClick={() => {
                    // Track back navigation
                    posthog.capture('onboarding_back_navigation', {
                      from_step: 'demo_closet_selection',
                      to_step: 'closet_type_selection',
                      gender: selectedGender
                    });
                    setClosetType(null);
                    setDemoClosets([]);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Choose Path
                </button>

                <div className="text-center mb-6">
                  <h3 className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                    Choose Style Collection
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Add pre-curated collection into your closet
                  </p>
                </div>

                <div className="max-w-xs sm:max-w-md mx-auto">
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    {demoClosets.map((closet) => (
                      <button
                        key={closet.id}
                        onClick={() => handleDemoClosetSelection(closet.id)}
                        disabled={isLoading}
                        className="group relative overflow-hidden rounded-xl border-2 border-gray-100 hover:border-purple-200 transition-all duration-300 bg-white text-left w-full"
                      >
                        <div className="relative w-full pb-[133.33%]">
                          <div 
                            className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                            style={{ 
                              backgroundImage: `url(${STYLE_IMAGES[closet.name] || ''})`,
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/30 group-hover:from-black/60 group-hover:via-black/30 group-hover:to-black/20 transition-colors" />
                          <div className="absolute inset-0 p-2 sm:p-4 flex flex-col justify-between text-white z-10">
                            <div className="text-xs sm:text-sm text-white/80 mt-auto">
                              <div>
                                <h3 className="font-semibold text-sm sm:text-base md:text-lg tracking-tight">
                                  {closet.name}
                                </h3>
                                <p className="text-xs sm:text-sm text-white/90 mt-1">
                                  {closet.outfits_count} items
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {isLoading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                      <span className="text-gray-800">Setting up your closet...</span>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : closetType === 'demo' && selectedGender && !isLoading ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4 text-center"
              >
                <button
                  onClick={() => {
                    // Track back navigation from no demo closets available
                    posthog.capture('onboarding_back_navigation', {
                      from_step: 'demo_closets_unavailable',
                      to_step: 'closet_type_selection',
                      gender: selectedGender
                    });
                    setClosetType(null);
                    setDemoClosets([]);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4 mx-auto"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Choose Path
                </button>
                <p className="text-gray-700 text-lg">
                  Demo closets are coming soon for {selectedGender}.
                </p>
                <Button
                  onClick={handleEmptyClosetSelect}
                  className="relative overflow-hidden rounded-2xl border-2 border-gray-200 bg-white text-gray-800 py-3 px-6 mx-auto hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span className="text-base sm:text-lg font-semibold">Start with Empty Closet</span>
                  </div>
                </Button>
              </motion.div>
            ) : closetType === 'demo' && isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">Loading demo closets for {selectedGender}...</p>
              </div>
            ) : null}
          </div>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="sm:max-w-[425px] max-w-[90vw]">
            <DialogHeader>
              <DialogTitle>
                Ready to Start?
              </DialogTitle>
              <DialogDescription className="pt-3">
                {confirmationType === 'demo' 
                  ? `Demo collection will be added to your closet. You can delete them or add your own anytime.`
                  : "You'll start with an empty closet! "}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  // Track confirmation dialog cancel
                  posthog.capture('onboarding_confirmation_cancelled', {
                    confirmation_type: confirmationType,
                    gender: selectedGender,
                    step: 'choose_path'
                  });
                  setShowConfirmDialog(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // Track confirmation dialog confirm
                  posthog.capture('onboarding_confirmation_confirmed', {
                    confirmation_type: confirmationType,
                    gender: selectedGender,
                    demo_collection_id: confirmationType === 'demo' ? selectedDemoCloset : null
                  });
                  handleConfirm();
                }}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-purple-500 hover:to-blue-600"
                disabled={confirmationType === 'demo' && isLoading}
              >
                {confirmationType === 'demo' && isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Setting up...
                  </div>
                ) : (
                  "Let's Begin!"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
};

export default WelcomeOnboarding; 